import React, { useRef, useState } from 'react';
import { useFields } from './State';

const PendingRemovalOption = ({ dropdownOption, namePrefix }) => {
  const { undoRemoveOption } = useFields();

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
      <input type="hidden" name={`${namePrefix}[_destroy]`} value="1" />
      <input type="hidden" name={`${namePrefix}[id]`} value={dropdownOption.id} />

      <div className="flex items-center gap-2">
        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
        </svg>
        <span className="text-sm text-red-700">"{dropdownOption.value || 'Option'}" will be removed</span>
      </div>
      <button
        onClick={(e) => {
          e.preventDefault();
          undoRemoveOption(dropdownOption);
        }}
        type="button"
        className="text-xs font-medium text-red-600 hover:text-red-700 hover:underline"
      >
        Undo
      </button>
    </div>
  );
};

const Option = (props) => {
  const { removeOption, state } = useFields();
  const { dropdownOption, index, fieldIndex, onDragStart, onDragOver, onDragEnd, onDrop, isDragging, isDragOver } = props;
  const namePrefix = `${state.resource_name}[form_fields_attributes][${fieldIndex}][form_select_options_attributes][${index}]`;

  const handleRemoveClick = (e) => {
    e.preventDefault();
    removeOption(dropdownOption);
  };

  if (dropdownOption.destroy) {
    return <PendingRemovalOption dropdownOption={dropdownOption} namePrefix={namePrefix} />;
  }

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
        isDragging
          ? 'opacity-40 border-dashed border-blue-300 bg-blue-50'
          : isDragOver
            ? 'border-blue-400 bg-blue-50 shadow-sm'
            : dropdownOption.isNew
              ? 'bg-blue-50 border-blue-200'
              : 'bg-white border-gray-200'
      }`}
    >
      {dropdownOption.id && (
        <input type="hidden" name={`${namePrefix}[id]`} value={dropdownOption.id} />
      )}
      <input type="hidden" name={`${namePrefix}[position]`} value={index + 1} />

      <div
        className="flex-shrink-0 text-gray-400 cursor-grab active:cursor-grabbing hover:text-gray-600 transition-colors"
        title="Drag to reorder"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path>
        </svg>
      </div>

      <input
        autoFocus={dropdownOption.isNew}
        name={`${namePrefix}[value]`}
        defaultValue={dropdownOption.value}
        disabled={dropdownOption.disable_change}
        placeholder="Enter option value..."
        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-colors disabled:bg-gray-100"
      />

      {dropdownOption.isNew && (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
          New
        </span>
      )}

      <button
        onClick={handleRemoveClick}
        type="button"
        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
        title={dropdownOption.isNew ? 'Discard' : 'Remove option'}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>
  );
};

export const DropDownOptions = (props) => {
  const { addOption, reorderOptions, state } = useFields();
  const { field, fieldIndex } = props;
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const options = state.dropdown_options.filter(
    (o) => o?.form_field_id === field.id || o?.fieldObjectId == field.objectId
  );

  const activeOptions = options.filter(o => !o.destroy);

  const handleAddOption = (e) => {
    e.preventDefault();
    addOption(field);
  };

  const handleDragStart = (index) => (e) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== dragIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (toIndex) => (e) => {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== toIndex) {
      reorderOptions(field, dragIndex, toIndex);
    }
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className="bg-blue-50/50 rounded-lg p-4 border border-blue-100">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"></path>
          </svg>
          <span className="text-sm font-medium text-gray-700">Dropdown Options</span>
          <span className="text-xs text-gray-500">({activeOptions.length} option{activeOptions.length !== 1 ? 's' : ''})</span>
        </div>
        <button
          onClick={handleAddOption}
          type="button"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
          Add Option
        </button>
      </div>

      {options.length === 0 ? (
        <div className="text-center py-4 text-sm text-gray-500">
          <p>No options yet. Add your first dropdown option above.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {options.map((o, idx) => (
            <Option
              key={o.objectId || idx}
              index={idx}
              fieldIndex={fieldIndex}
              dropdownOption={o}
              onDragStart={handleDragStart(idx)}
              onDragOver={handleDragOver(idx)}
              onDrop={handleDrop(idx)}
              onDragEnd={handleDragEnd}
              isDragging={dragIndex === idx}
              isDragOver={dragOverIndex === idx}
            />
          ))}
        </div>
      )}

      <p className="mt-3 text-xs text-gray-500 flex items-center gap-1">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        Drag options to reorder. Users will see them in this order.
      </p>
    </div>
  );
};
